import { useState, useCallback, useRef } from 'react';
import { toast } from 'sonner';
import { api } from '../../../../../../api/client';
import type { Perfil } from '../../../types';

interface UseCatalogCrudOptions {
  masterToken: string;
  catalog: Perfil[];
  selectedPerfilId: string;
  setSelectedPerfilId: (id: string) => void;
  selectedTopicoId: string;
  setSelectedTopicoId: (id: string) => void;
  selectedSubtopicoId: string;
  setSelectedSubtopicoId: (id: string) => void;
  loadCatalog: () => Promise<void>;
}

export function useCatalogCrud({
  masterToken,
  catalog,
  selectedPerfilId,
  setSelectedPerfilId,
  selectedTopicoId,
  setSelectedTopicoId,
  selectedSubtopicoId,
  setSelectedSubtopicoId,
  loadCatalog
}: UseCatalogCrudOptions) {
  // ── Guard universal contra double-click ──
  const busyRef = useRef<Set<string>>(new Set());
  const [, setGuardTick] = useState(0);
  const guardRun = useCallback(async <T,>(key: string, fn: () => Promise<T>): Promise<T | undefined> => {
    if (busyRef.current.has(key)) {
      console.warn(`[CatalogCrud] "${key}" bloqueado — já em execução`);
      return undefined;
    }
    busyRef.current.add(key);
    setGuardTick(t => t + 1);
    try {
      return await fn();
    } finally {
      busyRef.current.delete(key);
      setGuardTick(t => t + 1);
    }
  }, []);
  const isCrudBusy = useCallback((key?: string): boolean => {
    if (key) return busyRef.current.has(key);
    return busyRef.current.size > 0;
  }, []);

  // Inline creation inputs
  const [newPerfilName, setNewPerfilName] = useState('');
  const [newTopicoName, setNewTopicoName] = useState('');
  const [newSubtopicoName, setNewSubtopicoName] = useState('');

  // Inline editing state
  const [inlineEditingId, setInlineEditingId] = useState<string | null>(null);
  const [inlineEditingValue, setInlineEditingValue] = useState('');

  const getHeaders = useCallback(() => {
    return masterToken ? { Authorization: `Bearer ${masterToken}` } : {};
  }, [masterToken]);

  // ---- Perfil CRUD ----

  const handleCreatePerfil = async (nome: string) => guardRun('createPerfil', async () => {
    if (!nome.trim()) return;
    try {
      const id = nome.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
      const nextOrdem = catalog.length + 1;
      const res = await api.post('/ecad/perfil', {
        id,
        nome: nome.trim(),
        ordem: nextOrdem
      }, { headers: getHeaders() });

      if (res.data && res.data.ok) {
        toast.success('Perfil criado com sucesso!');
        setNewPerfilName('');
        await loadCatalog();
        setSelectedPerfilId(id);
      }
    } catch (err: any) {
      console.error(err);
      toast.error(`Erro ao criar perfil: ${err.message || 'Erro interno'}`);
    }
  });

  const handleRenamePerfil = async (id: string, novoNome: string) => guardRun('renamePerfil', async () => {
    if (!novoNome.trim()) return;
    try {
      const item = catalog.find(p => p.id === id);
      await api.post('/ecad/perfil', {
        id,
        nome: novoNome.trim(),
        ordem: item?.ordem || 1
      }, { headers: getHeaders() });
      toast.success('Perfil atualizado!');
      setInlineEditingId(null);
      await loadCatalog();
    } catch (err) {
      toast.error('Erro ao renomear perfil');
    }
  });

  const handleDeletePerfil = async (id: string) => {
    if (!window.confirm('Tem certeza que deseja excluir esta modalidade?')) return;
    return guardRun('deletePerfil', async () => {
      try {
        await api.delete(`/ecad/perfil/${id}`, { headers: getHeaders() });
        toast.success('Perfil excluído!');
        setSelectedPerfilId('');
        setSelectedTopicoId('');
        setSelectedSubtopicoId('');
        await loadCatalog();
      } catch (err: any) {
        toast.error(`Erro ao excluir: ${err.response?.data?.message || err.message}`);
      }
    });
  };

  const handleReorderPerfil = async (index: number, direction: 'up' | 'down') => guardRun('reorderPerfil', async () => {
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= catalog.length) return;

    const list = [...catalog].sort((a, b) => (a.ordem || 0) - (b.ordem || 0));
    const temp = list[index].ordem;
    list[index].ordem = list[targetIndex].ordem;
    list[targetIndex].ordem = temp;

    try {
      const headers = getHeaders();
      await api.post('/ecad/perfil', { id: list[index].id, nome: list[index].nome, ordem: list[index].ordem }, { headers });
      await api.post('/ecad/perfil', { id: list[targetIndex].id, nome: list[targetIndex].nome, ordem: list[targetIndex].ordem }, { headers });
      toast.success('Ordem atualizada!');
      await loadCatalog();
    } catch (err) {
      toast.error('Erro ao reordenar');
    }
  });

  // ---- Tópico CRUD ----

  const handleCreateTopico = async (titulo: string) => guardRun('createTopico', async () => {
    if (!titulo.trim() || !selectedPerfilId) return;
    try {
      const id = titulo.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '') + `-${Date.now().toString(36)}`;
      const activePerf = catalog.find(p => p.id === selectedPerfilId);
      const nextOrdem = (activePerf?.topicos?.length || 0) + 1;
      const res = await api.post('/ecad/topico', {
        id,
        perfilId: selectedPerfilId,
        titulo: titulo.trim(),
        ordem: nextOrdem
      }, { headers: getHeaders() });

      if (res.data && res.data.ok) {
        toast.success('Módulo criado com sucesso!');
        setNewTopicoName('');
        await loadCatalog();
        setSelectedTopicoId(id);
      }
    } catch (err: any) {
      console.error(err);
      toast.error(`Erro ao criar módulo: ${err.message || 'Erro interno'}`);
    }
  });

  const handleRenameTopico = async (id: string, novoTitulo: string) => guardRun('renameTopico', async () => {
    if (!novoTitulo.trim()) return;
    try {
      const activePerf = catalog.find(p => p.id === selectedPerfilId);
      const item = activePerf?.topicos.find(t => t.id === id);
      await api.post('/ecad/topico', {
        id,
        perfilId: selectedPerfilId,
        titulo: novoTitulo.trim(),
        ordem: item?.ordem || 1
      }, { headers: getHeaders() });
      toast.success('Módulo atualizado!');
      setInlineEditingId(null);
      await loadCatalog();
    } catch (err) {
      toast.error('Erro ao renomear módulo');
    }
  });

  const handleDeleteTopico = async (id: string) => {
    if (!window.confirm('Tem certeza que deseja excluir este módulo?')) return;
    return guardRun('deleteTopico', async () => {
      try {
        await api.delete(`/ecad/topico/${id}`, { headers: getHeaders() });
        toast.success('Módulo excluído!');
        setSelectedTopicoId('');
        setSelectedSubtopicoId('');
        await loadCatalog();
      } catch (err: any) {
        toast.error(`Erro ao excluir: ${err.response?.data?.message || err.message}`);
      }
    });
  };

  const handleReorderTopico = async (index: number, direction: 'up' | 'down') => guardRun('reorderTopico', async () => {
    const activePerf = catalog.find(p => p.id === selectedPerfilId);
    if (!activePerf?.topicos) return;

    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= activePerf.topicos.length) return;

    const list = [...activePerf.topicos].sort((a, b) => (a.ordem || 0) - (b.ordem || 0));
    const temp = list[index].ordem;
    list[index].ordem = list[targetIndex].ordem;
    list[targetIndex].ordem = temp;

    try {
      const headers = getHeaders();
      await api.post('/ecad/topico', { id: list[index].id, perfilId: selectedPerfilId, titulo: list[index].titulo, ordem: list[index].ordem }, { headers });
      await api.post('/ecad/topico', { id: list[targetIndex].id, perfilId: selectedPerfilId, titulo: list[targetIndex].titulo, ordem: list[targetIndex].ordem }, { headers });
      toast.success('Ordem atualizada!');
      await loadCatalog();
    } catch (err) {
      toast.error('Erro ao reordenar');
    }
  });

  // ---- Subtópico CRUD ----

  const handleCreateSubtopico = async (titulo: string) => guardRun('createSubtopico', async () => {
    if (!titulo.trim() || !selectedTopicoId) return;
    try {
      const id = titulo.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '') + `-${Date.now().toString(36)}`;
      const activePerf = catalog.find(p => p.id === selectedPerfilId);
      const activeTop = activePerf?.topicos?.find(t => t.id === selectedTopicoId);
      const nextOrdem = (activeTop?.subtopicos?.length || 0) + 1;
      const res = await api.post('/ecad/roteiro', {
        id,
        topicoId: selectedTopicoId,
        titulo: titulo.trim(),
        descricao: '',
        ordem: nextOrdem,
        status: 'RASCUNHO'
      }, { headers: getHeaders() });

      if (res.data && res.data.ok) {
        toast.success('Aula criada com sucesso!');
        setNewSubtopicoName('');
        await loadCatalog();
        setSelectedSubtopicoId(id);
      }
    } catch (err: any) {
      console.error(err);
      toast.error(`Erro ao criar aula: ${err.message || 'Erro interno'}`);
    }
  });

  const handleRenameSubtopico = async (id: string, novoTitulo: string) => guardRun('renameSubtopico', async () => {
    if (!novoTitulo.trim()) return;
    try {
      const activePerf = catalog.find(p => p.id === selectedPerfilId);
      const activeTop = activePerf?.topicos.find(t => t.id === selectedTopicoId);
      const item = activeTop?.subtopicos.find(s => s.id === id);
      await api.post('/ecad/roteiro', {
        id,
        topicoId: selectedTopicoId,
        titulo: novoTitulo.trim(),
        descricao: item?.descricao || '',
        ordem: item?.ordem || 1,
        videoUrl: item?.videoUrl || undefined,
        pdfUrl: item?.pdfUrl || undefined,
        legendaUrl: item?.legendaUrl || undefined,
        roteiroTexto: item?.roteiroTexto || undefined,
        roteiroEventos: item?.roteiroEventos || undefined,
        status: item?.status || 'RASCUNHO'
      }, { headers: getHeaders() });
      toast.success('Aula atualizada!');
      setInlineEditingId(null);
      await loadCatalog();
    } catch (err) {
      toast.error('Erro ao renomear aula');
    }
  });

  const handleDeleteSubtopico = async (id: string) => {
    if (!window.confirm('Tem certeza que deseja excluir esta aula?')) return;
    return guardRun('deleteSubtopico', async () => {
      try {
        await api.delete(`/ecad/roteiro/${id}`, { headers: getHeaders() });
        toast.success('Aula excluída!');
        setSelectedSubtopicoId('');
        await loadCatalog();
      } catch (err: any) {
        toast.error(`Erro ao excluir: ${err.response?.data?.message || err.message}`);
      }
    });
  };

  const handleReorderSubtopico = async (index: number, direction: 'up' | 'down') => guardRun('reorderSubtopico', async () => {
    const activePerf = catalog.find(p => p.id === selectedPerfilId);
    const activeTop = activePerf?.topicos.find(t => t.id === selectedTopicoId);
    if (!activeTop?.subtopicos) return;

    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= activeTop.subtopicos.length) return;

    const list = [...activeTop.subtopicos].sort((a, b) => (a.ordem || 0) - (b.ordem || 0));
    const temp = list[index].ordem;
    list[index].ordem = list[targetIndex].ordem;
    list[targetIndex].ordem = temp;

    try {
      const headers = getHeaders();
      await api.post('/ecad/roteiro', {
        id: list[index].id,
        topicoId: selectedTopicoId,
        titulo: list[index].titulo,
        descricao: list[index].descricao || '',
        ordem: list[index].ordem,
        videoUrl: list[index].videoUrl || undefined,
        pdfUrl: list[index].pdfUrl || undefined,
        legendaUrl: list[index].legendaUrl || undefined,
        status: list[index].status || 'RASCUNHO'
      }, { headers });

      await api.post('/ecad/roteiro', {
        id: list[targetIndex].id,
        topicoId: selectedTopicoId,
        titulo: list[targetIndex].titulo,
        descricao: list[targetIndex].descricao || '',
        ordem: list[targetIndex].ordem,
        videoUrl: list[targetIndex].videoUrl || undefined,
        pdfUrl: list[targetIndex].pdfUrl || undefined,
        legendaUrl: list[targetIndex].legendaUrl || undefined,
        status: list[targetIndex].status || 'RASCUNHO'
      }, { headers });

      toast.success('Ordem atualizada!');
      await loadCatalog();
    } catch (err) {
      toast.error('Erro ao reordenar');
    }
  });

  return {
    // Guard status
    isCrudBusy,
    // Creation inputs
    newPerfilName, setNewPerfilName,
    newTopicoName, setNewTopicoName,
    newSubtopicoName, setNewSubtopicoName,
    // Inline editing
    inlineEditingId, setInlineEditingId,
    inlineEditingValue, setInlineEditingValue,
    // Perfil handlers
    handleCreatePerfil,
    handleRenamePerfil,
    handleDeletePerfil,
    handleReorderPerfil,
    // Tópico handlers
    handleCreateTopico,
    handleRenameTopico,
    handleDeleteTopico,
    handleReorderTopico,
    // Subtópico handlers
    handleCreateSubtopico,
    handleRenameSubtopico,
    handleDeleteSubtopico,
    handleReorderSubtopico
  };
}
